/**
 * Left-edge elevation map of detected floor levels.
 *
 * One panel with an elevation ruler on the left (a tick + label at every
 * level's floor line) and one bar per proposed level on the right. Bars share
 * a common z-scale: vertical position and height map the real floor→ceiling
 * range, width scales with the level's detected area, and levels overlapping
 * in z (mezzanines, split-levels) move into side-by-side lanes. Hover and
 * click mirror the 3D floor volumes — the owning tool wires the callbacks to
 * the same handlers.
 *
 * The panel morphs into a small icon button (and back) the way the secondary
 * render-view widget does: one container animates between the button rect
 * and the panel rect while glyph and contents cross-fade. Tools choose the
 * default via `FloorBarsOptions.defaultCollapsed`, and the user's toggle wins
 * for the rest of the session. On narrow viewports the bar strip caps itself
 * to a fraction of the host width and scrolls.
 */

import type { DetectedFloorLevel } from './floorDetection';

export interface FloorBarsCallbacks {
  /** Pointer entered (index) / left (null) a bar. */
  onHover: (floorIndex: number | null) => void;
  /** Bar clicked. */
  onClick: (floorIndex: number) => void;
  /** Bar double-clicked (a real double click; two separate clicks don't count). */
  onDoubleClick?: (floorIndex: number) => void;
  /** An already added level's bar clicked; the index is into the added levels (the tool previews it again). */
  onAddedClick?: (addedIndex: number) => void;
}

export interface FloorBarsOptions {
  /**
   * Start minimized to the icon button instead of the full panel. Tools whose
   * job is not the floors themselves (room/wall/… selection) pass true so the
   * panel doesn't claim viewport space unasked; the floor tools keep it open.
   * A user's explicit expand/collapse click overrides this default for the
   * rest of the session, across all tools.
   */
  defaultCollapsed?: boolean;
  /**
   * Draw the proposed levels grey, so the added ones (green) stand out. For a
   * flow that adds floors from proposals; elsewhere the bars are the floors
   * themselves and stay green.
   */
  mutedProposals?: boolean;
}

/**
 * The user's last explicit expand/collapse choice this session. Overrides
 * every tool's `defaultCollapsed` once set — the panel shouldn't pop back
 * open (or shut) on each tool switch after the user decided.
 */
let sessionCollapsedOverride: boolean | null = null;

/** Marker attribute: lets the 3D hover handler ignore events from the overlay. */
export const FLOOR_BARS_OVERLAY_ATTR = 'data-floor-bars-overlay';

/** True when a DOM event originated inside a floor-bars overlay. */
export function isFloorBarsEvent(event: Event): boolean {
  return event.target instanceof Element && event.target.closest(`[${FLOOR_BARS_OVERLAY_ATTR}]`) !== null;
}

const PANEL_HEIGHT_FRACTION = 0.55;
const PANEL_MIN_HEIGHT_PX = 140;
/**
 * Distance from the viewport's left edge — the 16px inset shared by the
 * top-left render-view button, the hint bar and the bottom view toggle, so
 * the panel lines up with the controls above and below it.
 */
const PANEL_LEFT_PX = 16;
/** Tools bar overlaying the top of the viewport (as in the render-view widget's MorphShell). */
const TOOLS_BAR_HEIGHT_PX = 52;
/** The render-view widget button: 40px square, 16px below the tools bar. */
const WIDGET_BUTTON_SIZE_PX = 40;
/**
 * Anchor just under that button with the same 16px gap, so the left-edge
 * controls stack: widget button, then the floor panel growing downward.
 */
const PANEL_TOP_PX = TOOLS_BAR_HEIGHT_PX + 16 + WIDGET_BUTTON_SIZE_PX + 16;
const PANEL_PADDING_X_PX = 10;
const PANEL_PADDING_Y_PX = 18;
const RULER_WIDTH_PX = 46;
const RULER_GAP_PX = 8;
const MIN_BAR_WIDTH_PX = 18;
const MAX_BAR_WIDTH_PX = 72;
const MIN_BAR_HEIGHT_PX = 10;
const LANE_GAP_PX = 7;
/** Widest the bar strip may grow; more lanes than fit scroll horizontally. */
const MAX_BARS_VIEW_WIDTH_PX = 180;
/**
 * On narrow viewports the panel additionally caps itself to this fraction of
 * the host width (the bar strip shrinks and scrolls), so it never dominates a
 * small screen. The ResizeObserver relayout keeps this live.
 */
const MAX_PANEL_WIDTH_FRACTION = 0.3;
/** Square icon button the panel minimizes into — same size as the render-view toggle. */
const COLLAPSED_BUTTON_SIZE_PX = 40;
/** Morph duration between button and panel rects (matches the render-view widget). */
const MORPH_MS = 300;
/** Glyph / contents cross-fade duration. */
const FADE_MS = 150;
/**
 * Dedicated strip below the bars where the horizontal scrollbar lives (inside
 * the panel's bottom padding). Always reserved, so bars are never occluded
 * and nothing jumps when the scrollbar appears.
 */
const SCROLLBAR_SPACE_PX = 10;
/** Minimum vertical distance between two ruler labels before they get nudged apart. */
const LABEL_MIN_SPACING_PX = 12;
const BRAND_GREEN = '#59CD90';
/** Also Tailwind's neon-green-300 — the hover ring of the render-view widget button. */
const BRAND_GREEN_LIGHT = '#b5ffa1';

/**
 * Surface colors, mirroring the render-view widget's shell (its BACKGROUND_HEX
 * and `ring-black/30 dark:ring-white/15` / `text-gray-800 dark:text-gray-200`
 * classes) so the two top-left controls read as one family.
 */
const THEME = {
  dark: { background: '#313131', ring: 'rgba(255, 255, 255, 0.15)', glyph: '#ffffff', text: '255, 255, 255' },
  light: { background: '#cdcdcd', ring: 'rgba(0, 0, 0, 0.3)', glyph: '#1f2937', text: '31, 41, 55' },
} as const;

type Theme = (typeof THEME)[keyof typeof THEME];
type Phase = 'closed' | 'opening' | 'open' | 'closing';

/** The app toggles dark mode via a `dark` class on <html> (Tailwind). */
function readTheme(): Theme {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? THEME.dark : THEME.light;
}

/** The app's floor icon (assets/icons/Floor.svg), stroked in currentColor. */
const FLOOR_ICON_SVG = `
  <svg width="22" height="21" viewBox="0 0 23 22" fill="none" aria-hidden="true" style="stroke-linecap:round;stroke-linejoin:round;">
    <g transform="matrix(0.956305,0,0,0.906672,0.186389,0.324632)">
      <g transform="matrix(1.066293,0,0,1.194374,1.905182,-2.990128)">
        <path d="M17.79,11.08L17.79,19.34C17.79,19.34 17.71,21.25 16.02,21.37C14.33,21.49 2.43,21.37 2.43,21.37C1.37,21.37 0.51,20.51 0.51,19.44L0.51,10.75C0.51,10.18 0.76,9.64 1.19,9.28L7.9,3.49C8.62,2.88 9.66,2.88 10.38,3.49L17.1,9.28C17.53,9.65 17.78,10.19 17.78,10.75" stroke="currentColor" stroke-width="1.51"/>
        <path d="M3.29,11.08L15.2,11.08" stroke="currentColor" stroke-width="1.51"/>
        <path d="M15.2,16.54L3.29,16.52" stroke="currentColor" stroke-width="1.51"/>
      </g>
    </g>
  </svg>`;

const SCROLLBAR_CLASS = 'floor-bars-scroll';
const SCROLLBAR_STYLE_ID = 'floor-bars-scrollbar-style';

/** Inject the webkit scrollbar rules once per document. */
function ensureScrollbarStylesheet(): void {
  if (document.getElementById(SCROLLBAR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SCROLLBAR_STYLE_ID;
  style.textContent = `
    .${SCROLLBAR_CLASS}::-webkit-scrollbar { height: 6px; background: transparent; }
    .${SCROLLBAR_CLASS}::-webkit-scrollbar-track { background: transparent; }
    .${SCROLLBAR_CLASS}::-webkit-scrollbar-thumb { background: rgba(89, 205, 144, 0.4); border-radius: 3px; }
    .${SCROLLBAR_CLASS}::-webkit-scrollbar-thumb:hover { background: rgba(89, 205, 144, 0.65); }
  `;
  document.head.appendChild(style);
}

interface BarLayout {
  /** Index into the proposed levels; -1 for a level already added. */
  floorIndex: number;
  /** Index into the added levels; -1 for a proposal. */
  addedIndex: number;
  lane: number;
  widthPx: number;
  /** z-range of the bar (floor → ceiling). */
  zLow: number;
  zHigh: number;
  isSubLevel: boolean;
  title: string;
}

/**
 * Area driving a level's bar width. Only room patches yield a MEASURED area;
 * the footprint/bounds fallbacks are layout hints (persisted floor groups
 * carry placeholder 10×10 bounds) and must never be presented as a
 * measurement — see `isMeasuredArea`.
 */
function levelAreaM2(level: DetectedFloorLevel): number {
  if (isMeasuredArea(level)) {
    return level.rooms.reduce((sum, room) => sum + room.areaM2, 0);
  }
  // Per-floor persisted footprint (shoelace — corners may form a rotated rect)
  if (level.footprintCorners && level.footprintCorners.length >= 3) {
    let doubled = 0;
    const corners = level.footprintCorners;
    for (let i = 0; i < corners.length; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      doubled += a.x * b.y - b.x * a.y;
    }
    return Math.abs(doubled) / 2;
  }
  const box = level.footprint ?? level.floor.bounds;
  return Math.max(0, (box.max.x - box.min.x) * (box.max.y - box.min.y));
}

/** Whether the level's area comes from detected room patches (a real measurement). */
function isMeasuredArea(level: DetectedFloorLevel): level is DetectedFloorLevel & { rooms: NonNullable<DetectedFloorLevel['rooms']> } {
  return level.rooms !== undefined && level.rooms.length > 0;
}

export class FloorBarsOverlay {
  private readonly host: HTMLElement;
  private readonly callbacks: FloorBarsCallbacks;
  /** The one element that morphs between icon button and panel. */
  private readonly container: HTMLDivElement;
  /** Floor glyph, shown while collapsed / morphing. */
  private readonly glyph: HTMLDivElement;
  /** Ruler + bars + minimize control, shown while open. */
  private readonly content: HTMLDivElement;
  private bars = new Map<number, HTMLDivElement>();
  /** Greyed-out bars of the added levels, by index into them. */
  private addedBars = new Map<number, HTMLDivElement>();
  private hoveredIndex = -1;
  private previewedIndex = -1;
  /** The added level previewed again (-1 = none): its bar gets the selection highlight. */
  private previewedAddedIndex = -1;
  private readonly defaultCollapsed: boolean;
  private readonly mutedProposals: boolean;
  private theme: Theme = readTheme();

  /** Morph state; seeded from the collapsed default on first render (no morph plays). */
  private phase: Phase | null = null;
  private phaseTimer: number | null = null;
  private phaseFrame: number | null = null;
  /** Panel rect from the last layout — the morph's open-state target. */
  private panelWidth = COLLAPSED_BUTTON_SIZE_PX;
  private panelHeight = COLLAPSED_BUTTON_SIZE_PX;

  /** Last levels shown, kept for resize-driven relayouts. */
  private lastLevels: DetectedFloorLevel[] | null = null;
  private lastAddedLevels: DetectedFloorLevel[] = [];
  private readonly resizeObserver: ResizeObserver | null = null;
  private readonly themeObserver = new MutationObserver(() => this.queueRelayout());
  private relayoutQueued = false;

  constructor(host: HTMLElement, callbacks: FloorBarsCallbacks, options?: FloorBarsOptions) {
    this.host = host;
    this.callbacks = callbacks;
    this.defaultCollapsed = options?.defaultCollapsed ?? false;
    this.mutedProposals = options?.mutedProposals ?? false;

    // Keep the panel's height RELATIVE to the viewport: re-layout when the
    // host resizes (window resize, panel collapse, resolution change).
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.queueRelayout());
      this.resizeObserver.observe(host);
    }

    this.container = document.createElement('div');
    this.container.setAttribute(FLOOR_BARS_OVERLAY_ATTR, '');
    const style = this.container.style;
    style.position = 'absolute';
    style.left = `${PANEL_LEFT_PX}px`;
    style.top = `${PANEL_TOP_PX}px`;
    style.zIndex = '1000';
    style.userSelect = 'none';
    style.overflow = 'hidden';
    // The app's global reset applies border-box; the layout below computes
    // content extents itself, so the panel is sized to content + padding.
    style.boxSizing = 'border-box';
    style.borderRadius = '6px';
    style.fontFamily = 'monospace';
    style.pointerEvents = 'auto';
    this.container.addEventListener('click', (event) => {
      // Only the collapsed button acts on a click — open, the bars and the
      // minimize control handle their own.
      if (this.phase !== 'closed') return;
      event.stopPropagation();
      this.setCollapsed(false);
    });
    this.container.addEventListener('keydown', (event) => {
      if (this.phase === 'closed' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.setCollapsed(false);
      }
    });
    this.container.addEventListener('mouseenter', () => {
      if (this.phase === 'closed') this.container.style.boxShadow = this.shellShadow(BRAND_GREEN_LIGHT);
    });
    this.container.addEventListener('mouseleave', () => {
      this.container.style.boxShadow = this.shellShadow(this.theme.ring);
    });

    this.glyph = document.createElement('div');
    this.glyph.style.position = 'absolute';
    this.glyph.style.inset = '0';
    this.glyph.style.display = 'flex';
    this.glyph.style.alignItems = 'center';
    this.glyph.style.justifyContent = 'center';
    this.glyph.style.pointerEvents = 'none';
    this.glyph.style.transition = `opacity ${FADE_MS}ms ease`;
    this.glyph.innerHTML = FLOOR_ICON_SVG;
    this.container.appendChild(this.glyph);

    this.content = document.createElement('div');
    this.content.style.position = 'absolute';
    this.content.style.inset = '0';
    this.content.style.transition = `opacity ${FADE_MS}ms ease`;
    this.container.appendChild(this.content);

    this.applyTheme();
  }

  /** Ring + drop shadow, like the widget shell's `ring-1 … shadow-md`. */
  private shellShadow(ring: string): string {
    return `0 0 0 1px ${ring}, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)`;
  }

  /** Text color with alpha in the current theme. */
  private text(alpha: number): string {
    return `rgba(${this.theme.text}, ${alpha})`;
  }

  private applyTheme(): void {
    this.theme = readTheme();
    this.container.style.background = this.theme.background;
    this.container.style.boxShadow = this.shellShadow(this.theme.ring);
    this.glyph.style.color = this.theme.glyph;
  }

  /**
   * Resolved live (not captured at construction): tools cache their overlay
   * instances, so a toggle made under another tool must still win here.
   */
  private isCollapsed(): boolean {
    return sessionCollapsedOverride ?? this.defaultCollapsed;
  }

  /**
   * Expand/minimize the panel with the morph. Remembered as the session-wide
   * preference so the choice survives tool switches and overlay rebuilds.
   */
  private setCollapsed(collapsed: boolean): void {
    if (this.isCollapsed() === collapsed) return;
    sessionCollapsedOverride = collapsed;
    if (!this.lastLevels || !this.container.parentElement) return;
    this.enterPhase(collapsed ? 'closing' : 'opening');
  }

  private clearPhaseTimers(): void {
    if (this.phaseTimer !== null) window.clearTimeout(this.phaseTimer);
    if (this.phaseFrame !== null) cancelAnimationFrame(this.phaseFrame);
    this.phaseTimer = null;
    this.phaseFrame = null;
  }

  /**
   * Drive the morph. `opening`/`closing` set the start rect first, swap to the
   * destination rect one frame later (so the CSS transition sees a delta),
   * and settle into `open`/`closed` once the transition has run.
   */
  private enterPhase(phase: Phase): void {
    this.clearPhaseTimers();
    this.phase = phase;
    this.applyPhaseStyles();

    if (phase === 'opening' || phase === 'closing') {
      const settled: Phase = phase === 'opening' ? 'open' : 'closed';
      this.phaseFrame = requestAnimationFrame(() => {
        this.phaseFrame = null;
        this.applyRect(settled);
      });
      this.phaseTimer = window.setTimeout(() => {
        this.phaseTimer = null;
        this.enterPhase(settled);
      }, MORPH_MS + 20);
    }
  }

  /** Size the container for the button (`closed`) or the panel (`open`). */
  private applyRect(target: Phase): void {
    const button = target === 'closed' || target === 'opening';
    this.container.style.width = `${button ? COLLAPSED_BUTTON_SIZE_PX : this.panelWidth}px`;
    this.container.style.height = `${button ? COLLAPSED_BUTTON_SIZE_PX : this.panelHeight}px`;
  }

  private applyPhaseStyles(): void {
    const phase = this.phase ?? 'closed';
    const isButton = phase === 'closed';
    const morphing = phase === 'opening' || phase === 'closing';

    // Animate only while morphing — an open panel must follow resizes instantly.
    this.container.style.transition = morphing ? `width ${MORPH_MS}ms ease-out, height ${MORPH_MS}ms ease-out, box-shadow 120ms ease` : 'box-shadow 120ms ease';
    this.applyRect(phase);

    this.container.style.cursor = isButton ? 'pointer' : 'default';
    if (isButton) {
      this.container.setAttribute('role', 'button');
      this.container.tabIndex = 0;
    } else {
      this.container.style.boxShadow = this.shellShadow(this.theme.ring);
      this.container.removeAttribute('role');
      this.container.removeAttribute('tabindex');
      this.container.removeAttribute('title');
      this.container.removeAttribute('aria-label');
    }

    // Glyph shows while collapsed and during the morph; contents only once open.
    this.glyph.style.opacity = isButton ? '1' : '0';
    this.content.style.opacity = phase === 'open' ? '1' : '0';
    this.content.style.pointerEvents = phase === 'open' ? 'auto' : 'none';
  }

  /** Coalesce resize bursts into one relayout per frame, keeping bar states. */
  private queueRelayout(): void {
    if (this.relayoutQueued || !this.lastLevels || !this.container.parentElement) return;
    this.relayoutQueued = true;
    requestAnimationFrame(() => {
      this.relayoutQueued = false;
      if (!this.lastLevels || !this.container.parentElement) return;
      const hovered = this.hoveredIndex;
      const previewed = this.previewedIndex;
      const previewedAdded = this.previewedAddedIndex;
      this.setLevels(this.lastLevels, this.lastAddedLevels);
      this.setHovered(hovered);
      this.setPreviewed(previewed);
      this.setPreviewedAdded(previewedAdded);
    });
  }

  /**
   * Rebuild the bars for the given levels (indices match availableFloors).
   * `addedLevels` (already turned into floors) stay on the map, never addable
   * again, so the user still sees where they sit among the proposals.
   */
  setLevels(levels: DetectedFloorLevel[], addedLevels: DetectedFloorLevel[] = []): void {
    this.lastLevels = levels;
    this.lastAddedLevels = addedLevels;
    this.content.innerHTML = '';
    this.bars.clear();
    this.addedBars.clear();
    this.hoveredIndex = -1;
    this.previewedIndex = -1;
    this.previewedAddedIndex = -1;
    this.applyTheme();

    const drawable = [...levels.map((level, floorIndex) => ({ level, floorIndex, addedIndex: -1 })), ...addedLevels.map((level, addedIndex) => ({ level, floorIndex: -1, addedIndex }))].filter(({ level }) => level.ceiling !== null);
    if (drawable.length === 0) {
      this.detach();
      return;
    }

    const label = `Show floor panel (${drawable.length} ${drawable.length === 1 ? 'level' : 'levels'})`;
    if (this.isCollapsed()) {
      this.container.title = label;
      this.container.setAttribute('aria-label', label);
    }

    // Shared z scale across all levels.
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const { level } of drawable) {
      minZ = Math.min(minZ, level.floor.height);
      maxZ = Math.max(maxZ, level.ceiling?.height ?? level.floor.height);
    }
    const span = Math.max(maxZ - minZ, 0.1);
    // Height relative to the viewport, but the panel grows DOWN from its
    // anchor — never past the bottom inset the other edge controls keep.
    const hostHeight = this.host.clientHeight;
    const maxPanelHeight = hostHeight - PANEL_TOP_PX - 2 * PANEL_PADDING_Y_PX - PANEL_LEFT_PX;
    const panelHeight = Math.max(PANEL_MIN_HEIGHT_PX, Math.min(hostHeight * PANEL_HEIGHT_FRACTION, maxPanelHeight));
    const scale = panelHeight / span;
    const yOfZ = (z: number): number => panelHeight - (z - minZ) * scale;

    // Widths ∝ area, relative to the largest level.
    const areas = drawable.map(({ level }) => levelAreaM2(level));
    const maxArea = Math.max(...areas, 1e-6);

    // Lane assignment in AREA-DESCENDING order: the biggest levels claim the
    // leftmost lanes (always visible without scrolling); smaller overlapping
    // levels (mezzanines) spill right into the scrollable strip. A level
    // joins the first lane where it overlaps none of that lane's bars in z.
    const sorted = drawable.map((entry, i) => ({ ...entry, area: areas[i] })).sort((a, b) => b.area - a.area);
    const laneIntervals: Array<Array<{ zLow: number; zHigh: number }>> = [];
    const layouts: BarLayout[] = [];
    for (const { level, floorIndex, addedIndex, area } of sorted) {
      const zLow = level.floor.height;
      const zHigh = level.ceiling?.height ?? level.floor.height;
      let lane = laneIntervals.findIndex((intervals) => intervals.every((other) => zLow >= other.zHigh - 0.01 || zHigh <= other.zLow + 0.01));
      if (lane === -1) {
        lane = laneIntervals.length;
        laneIntervals.push([]);
      }
      laneIntervals[lane].push({ zLow, zHigh });
      layouts.push({
        floorIndex,
        addedIndex,
        lane,
        widthPx: MIN_BAR_WIDTH_PX + (area / maxArea) * (MAX_BAR_WIDTH_PX - MIN_BAR_WIDTH_PX),
        zLow,
        zHigh,
        isSubLevel: level.isSubLevel === true,
        // Floor name first; area only when actually measured from detected
        // room patches — placeholder bounds must not read as a real m² figure.
        title: `${floorIndex === -1 ? 'Added · ' : ''}${level.name ? `${level.name} · ` : ''}${isMeasuredArea(level) ? `${area.toFixed(1)} m² · ` : ''}${zLow.toFixed(2)} → ${zHigh.toFixed(2)} m`,
      });
    }

    // Lane x offsets from the widest bar per lane.
    const laneWidth: number[] = [];
    for (const layout of layouts) {
      laneWidth[layout.lane] = Math.max(laneWidth[layout.lane] ?? 0, layout.widthPx);
    }
    const laneOffset: number[] = [];
    let barsWidth = 0;
    for (let lane = 0; lane < laneWidth.length; lane++) {
      laneOffset[lane] = barsWidth;
      barsWidth += laneWidth[lane] + LANE_GAP_PX;
    }
    barsWidth = Math.max(0, barsWidth - LANE_GAP_PX);

    // Panel sized to content + padding (border-box): too many lanes scroll
    // inside the capped bar strip instead of growing the panel. On narrow
    // viewports the strip additionally yields to the fractional cap (but
    // never below one bar) so the panel stays proportionate to the screen.
    const hostWidth = this.host.clientWidth || Number.POSITIVE_INFINITY;
    const fixedWidth = RULER_WIDTH_PX + RULER_GAP_PX + 2 * PANEL_PADDING_X_PX;
    const responsiveCap = Math.max(MIN_BAR_WIDTH_PX, hostWidth * MAX_PANEL_WIDTH_FRACTION - fixedWidth);
    const barsViewWidth = Math.min(barsWidth, MAX_BARS_VIEW_WIDTH_PX, responsiveCap);
    this.panelWidth = fixedWidth + barsViewWidth;
    this.panelHeight = panelHeight + 2 * PANEL_PADDING_Y_PX;

    // ── Minimize control, in the top padding strip above the ruler ──
    const minimizeButton = document.createElement('button');
    minimizeButton.setAttribute('aria-label', 'Hide floor panel');
    minimizeButton.title = 'Hide floor panel';
    minimizeButton.style.position = 'absolute';
    minimizeButton.style.top = '2px';
    minimizeButton.style.right = '4px';
    minimizeButton.style.width = '14px';
    minimizeButton.style.height = '14px';
    minimizeButton.style.display = 'flex';
    minimizeButton.style.alignItems = 'center';
    minimizeButton.style.justifyContent = 'center';
    minimizeButton.style.padding = '0';
    minimizeButton.style.background = 'transparent';
    minimizeButton.style.border = 'none';
    minimizeButton.style.cursor = 'pointer';
    minimizeButton.style.color = this.text(0.9);
    minimizeButton.style.opacity = '0.55';
    minimizeButton.style.transition = 'opacity 120ms ease';
    minimizeButton.innerHTML = `
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M6.5 1.5 L3 5 L6.5 8.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    minimizeButton.addEventListener('mouseenter', () => {
      minimizeButton.style.opacity = '1';
    });
    minimizeButton.addEventListener('mouseleave', () => {
      minimizeButton.style.opacity = '0.55';
    });
    minimizeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.setCollapsed(true);
    });
    this.content.appendChild(minimizeButton);

    // ── Ruler: hairline + a tick and elevation label at each floor line ──
    const ruler = document.createElement('div');
    ruler.style.position = 'absolute';
    ruler.style.left = `${PANEL_PADDING_X_PX}px`;
    ruler.style.top = `${PANEL_PADDING_Y_PX}px`;
    ruler.style.width = `${RULER_WIDTH_PX}px`;
    ruler.style.height = `${panelHeight}px`;
    this.content.appendChild(ruler);

    const hairline = document.createElement('div');
    hairline.style.position = 'absolute';
    hairline.style.right = '0';
    hairline.style.top = '0';
    hairline.style.width = '1px';
    hairline.style.height = '100%';
    hairline.style.background = this.text(0.18);
    ruler.appendChild(hairline);

    const addRulerMark = (z: number, labelY: number, emphasized: boolean): void => {
      const tick = document.createElement('div');
      tick.style.position = 'absolute';
      tick.style.right = '0';
      tick.style.top = `${yOfZ(z) - 0.5}px`;
      tick.style.width = '7px';
      tick.style.height = '1px';
      tick.style.background = this.text(emphasized ? 0.8 : 0.55);
      ruler.appendChild(tick);

      const label = document.createElement('div');
      label.textContent = `${z.toFixed(2)}m`;
      label.style.position = 'absolute';
      label.style.right = '11px';
      label.style.top = `${labelY}px`;
      label.style.transform = 'translateY(-50%)';
      label.style.fontSize = '10px';
      label.style.color = this.text(emphasized ? 1 : 0.8);
      label.style.whiteSpace = 'nowrap';
      ruler.appendChild(label);
    };

    // Top of the axis: the highest detected ceiling's elevation.
    addRulerMark(maxZ, yOfZ(maxZ), true);
    let lastLabelY = yOfZ(maxZ);

    // Floor lines (levels sharing a floor share one label), top-down so the
    // de-overlap pass can nudge collisions downward.
    const floorLines = [...new Set(layouts.map((l) => l.zLow.toFixed(2)))].map((s) => Number(s)).sort((a, b) => b - a);
    for (const z of floorLines) {
      const labelY = Math.max(yOfZ(z), lastLabelY + LABEL_MIN_SPACING_PX);
      lastLabelY = labelY;
      addRulerMark(z, labelY, false);
    }

    // ── Bars, inside a capped horizontally-scrollable strip ──
    const barsViewport = document.createElement('div');
    barsViewport.style.position = 'absolute';
    barsViewport.style.left = `${PANEL_PADDING_X_PX + RULER_WIDTH_PX + RULER_GAP_PX}px`;
    barsViewport.style.top = `${PANEL_PADDING_Y_PX}px`;
    barsViewport.style.width = `${barsViewWidth}px`;
    // The extra strip keeps the scrollbar below the bars instead of over
    // their bottom edge; it eats into the bottom padding, not the bars.
    barsViewport.style.height = `${panelHeight + SCROLLBAR_SPACE_PX}px`;
    barsViewport.style.overflowX = barsWidth > barsViewWidth ? 'auto' : 'hidden';
    barsViewport.style.overflowY = 'hidden';
    // Minimal themed scrollbar: standard properties (Chromium/Firefox) plus
    // an injected webkit fallback for engines without scrollbar-color.
    barsViewport.classList.add(SCROLLBAR_CLASS);
    barsViewport.style.setProperty('scrollbar-width', 'thin');
    barsViewport.style.setProperty('scrollbar-color', 'rgba(89, 205, 144, 0.4) transparent');
    ensureScrollbarStylesheet();
    this.content.appendChild(barsViewport);

    const barsContent = document.createElement('div');
    barsContent.style.position = 'relative';
    barsContent.style.width = `${barsWidth}px`;
    barsContent.style.height = `${panelHeight}px`;
    barsViewport.appendChild(barsContent);

    for (const layout of layouts) {
      const bar = document.createElement('div');
      const heightPx = Math.max(MIN_BAR_HEIGHT_PX, (layout.zHigh - layout.zLow) * scale);
      bar.style.position = 'absolute';
      bar.style.left = `${laneOffset[layout.lane]}px`;
      bar.style.top = `${yOfZ(layout.zHigh)}px`;
      bar.style.width = `${layout.widthPx}px`;
      bar.style.height = `${heightPx}px`;
      bar.style.boxSizing = 'border-box';
      bar.style.border = `1.5px ${layout.isSubLevel ? 'dashed' : 'solid'} ${BRAND_GREEN}`;
      bar.style.borderRadius = '4px';
      bar.style.cursor = layout.floorIndex === -1 && !this.callbacks.onAddedClick ? 'default' : 'pointer';
      bar.style.transition = 'background 120ms ease, box-shadow 120ms ease, opacity 120ms ease, border-color 120ms ease';
      bar.title = layout.title;

      // In-bar text: the level's own interior height (elevations live on the
      // ruler). Skipped when the bar is too small to fit it legibly.
      if (heightPx >= 16 && layout.widthPx >= 34) {
        const heightLabel = document.createElement('div');
        heightLabel.textContent = `${(layout.zHigh - layout.zLow).toFixed(2)}m`;
        heightLabel.style.position = 'absolute';
        heightLabel.style.left = '0';
        heightLabel.style.right = '0';
        heightLabel.style.top = '50%';
        heightLabel.style.transform = 'translateY(-50%)';
        heightLabel.style.textAlign = 'center';
        heightLabel.style.fontSize = '10px';
        heightLabel.style.color = this.text(0.92);
        heightLabel.style.textShadow = `0 0 4px ${this.theme.background}`;
        heightLabel.style.pointerEvents = 'none';
        heightLabel.style.whiteSpace = 'nowrap';
        heightLabel.style.overflow = 'hidden';
        bar.appendChild(heightLabel);
      }

      const floorIndex = layout.floorIndex;
      if (floorIndex === -1) {
        // Already added: never added again. A click shows its preview again
        // (styled in applyStates).
        const addedIndex = layout.addedIndex;
        this.addedBars.set(addedIndex, bar);
        bar.addEventListener('click', (event) => {
          event.stopPropagation();
          this.callbacks.onAddedClick?.(addedIndex);
        });
        bar.addEventListener('dblclick', (event) => event.stopPropagation());
        barsContent.appendChild(bar);
        continue;
      }
      bar.addEventListener('mouseenter', () => this.callbacks.onHover(floorIndex));
      bar.addEventListener('mouseleave', () => this.callbacks.onHover(null));
      bar.addEventListener('click', (event) => {
        event.stopPropagation();
        this.callbacks.onClick(floorIndex);
      });
      bar.addEventListener('dblclick', (event) => {
        event.stopPropagation();
        this.callbacks.onDoubleClick?.(floorIndex);
      });

      this.bars.set(floorIndex, bar);
      barsContent.appendChild(bar);
    }

    this.applyStates();

    // First render (or re-attach) settles straight into the collapsed default
    // without a morph; later relayouts keep whatever phase is running.
    if (!this.container.parentElement) {
      this.host.appendChild(this.container);
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.enterPhase(this.isCollapsed() ? 'closed' : 'open');
    } else {
      this.applyPhaseStyles();
    }
  }

  /** Mirror the 3D hover state (-1 = none). */
  setHovered(floorIndex: number): void {
    if (this.hoveredIndex === floorIndex) return;
    this.hoveredIndex = floorIndex;
    this.applyStates();
  }

  /** Mirror the preview state (-1 = none). */
  setPreviewed(floorIndex: number): void {
    if (this.previewedIndex === floorIndex) return;
    this.previewedIndex = floorIndex;
    this.applyStates();
  }

  /** Mirror an added level's renewed preview (-1 = none). */
  setPreviewedAdded(addedIndex: number): void {
    if (this.previewedAddedIndex === addedIndex) return;
    this.previewedAddedIndex = addedIndex;
    this.applyStates();
  }

  private applyStates(): void {
    const anyTarget = this.previewedIndex !== -1 || this.hoveredIndex !== -1 || this.previewedAddedIndex !== -1;
    // Added levels are the real floors now: green, the previewed one bright.
    for (const [addedIndex, bar] of this.addedBars) {
      this.styleBar(bar, 'green', addedIndex === this.previewedAddedIndex ? 'selected' : 'rest', anyTarget);
    }
    // Proposals: grey while they sit next to added levels (the auto-floor
    // flow), green where they are the floors themselves.
    const proposalTone = this.mutedProposals ? 'grey' : 'green';
    for (const [floorIndex, bar] of this.bars) {
      this.styleBar(bar, proposalTone, floorIndex === this.previewedIndex ? 'selected' : floorIndex === this.hoveredIndex ? 'hovered' : 'rest', anyTarget);
    }
  }

  private styleBar(bar: HTMLDivElement, tone: 'green' | 'grey', state: 'selected' | 'hovered' | 'rest', anyTarget: boolean): void {
    if (tone === 'green') {
      if (state === 'selected') {
        bar.style.background = `linear-gradient(180deg, rgba(181, 255, 161, 0.85), rgba(89, 205, 144, 0.75))`;
        bar.style.borderColor = BRAND_GREEN_LIGHT;
        bar.style.boxShadow = '0 0 10px rgba(181, 255, 161, 0.55)';
      } else if (state === 'hovered') {
        bar.style.background = `linear-gradient(180deg, rgba(89, 205, 144, 0.6), rgba(89, 205, 144, 0.4))`;
        bar.style.borderColor = BRAND_GREEN;
        bar.style.boxShadow = '0 0 8px rgba(89, 205, 144, 0.5)';
      } else {
        bar.style.background = `linear-gradient(180deg, rgba(89, 205, 144, 0.32), rgba(89, 205, 144, 0.14))`;
        bar.style.borderColor = BRAND_GREEN;
        bar.style.boxShadow = 'none';
      }
    } else if (state === 'selected') {
      bar.style.background = this.text(0.35);
      bar.style.borderColor = this.text(0.95);
      bar.style.boxShadow = `0 0 10px ${this.text(0.45)}`;
    } else if (state === 'hovered') {
      bar.style.background = this.text(0.22);
      bar.style.borderColor = this.text(0.7);
      bar.style.boxShadow = `0 0 8px ${this.text(0.3)}`;
    } else {
      bar.style.background = this.text(0.12);
      bar.style.borderColor = this.text(0.45);
      bar.style.boxShadow = 'none';
    }
    bar.style.opacity = state === 'rest' && anyTarget ? '0.35' : '1';
  }

  /** Remove all bars and detach from the viewport. */
  clear(): void {
    this.lastLevels = null;
    this.lastAddedLevels = [];
    this.content.innerHTML = '';
    this.bars.clear();
    this.addedBars.clear();
    this.previewedAddedIndex = -1;
    this.hoveredIndex = -1;
    this.previewedIndex = -1;
    this.detach();
  }

  private detach(): void {
    this.clearPhaseTimers();
    this.phase = null;
    this.themeObserver.disconnect();
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
